// import { routes } from '@/config/routes';

interface BreadcrumbItem {
  name: string;
  path: string;
  clickable: boolean;
}

interface BreadcrumbConfig {
  // 是否在面包屑中显示
  showInBreadcrumb?: boolean;
  // 是否可点击
  clickable?: boolean;
  // 自定义显示名称
  displayName?: string;
  // 点击跳转的目标路径（如果不指定，使用当前路径）
  targetPath?: string;
  // 是否为父级路由（没有独立页面）
  isParentRoute?: boolean;
}

// 根据路径查找路由配置
const findRouteByPath = (pathname: string, routes: any[]): any => {
  const segments = pathname.split('/').filter(Boolean);

  for (const route of routes) {
    // 处理根路径匹配
    if (route.path === `/${segments[0]}`) {
      if (segments.length === 1) {
        return route;
      }

      if (route.routes) {
        const subPath = segments.slice(1).join('/');
        return findRouteByPath(`/${subPath}`, route.routes);
      }
    }
  }

  return null;
};

// 根据完整路径查找路由配置（支持动态参数）
const findRouteByFullPath = (pathname: string, routes: any[]): any => {
  const segments = pathname.split('/').filter(Boolean);

  for (const route of routes) {
    // 处理根路径匹配
    if (route.path === `/${segments[0]}`) {
      if (route.routes) {
        // 在子路由中查找匹配的路径
        for (const subRoute of route.routes) {
          const subRoutePath = subRoute.path;
          const subRouteSegments = subRoutePath.split('/').filter(Boolean);

          // 检查路径是否匹配（支持动态参数）
          if (segments.length >= subRouteSegments.length + 1) { // +1 因为包含父路径
            let matches = true;
            for (let i = 0; i < subRouteSegments.length; i++) {
              const subRouteSegment = subRouteSegments[i];
              const pathSegment = segments[i + 1]; // +1 因为跳过父路径

              // 如果是动态参数（:id），跳过检查
              if (subRouteSegment.startsWith(':')) {
                continue;
              }

              // 如果路径段不匹配，则整个路由不匹配
              if (subRouteSegment !== pathSegment) {
                matches = false;
                break;
              }
            }

            if (matches) {
              return subRoute;
            }
          }
        }
      }
    }
  }

  return null;
};

// 生成面包屑导航
export const generateBreadcrumbFromRoutes = (pathname: string): BreadcrumbItem[] => {
  // 临时回退到原来的逻辑，避免报错
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbItems: BreadcrumbItem[] = [];

  // 获取URL参数来判断页面模式和来源
  const urlParams = new URLSearchParams(window.location.search);
  const mode = urlParams.get('mode');
  const customerId = urlParams.get('customerId');
  const id = urlParams.get('id'); // 获取id参数，用于判断是编辑还是新增
  const from = urlParams.get('from'); // 获取来源参数，用于判断是从哪个页面跳转过来的

  // 简单的路由名称映射
  const routeNameMap: Record<string, string> = {
    'user': '用户管理',
    'customer': '客户管理',
    'task': '任务管理',
    'myTask': '我的任务',
    'taskSupplement': '任务接单',
    'taskSupplementEdit': '编辑任务接单',
    'statistics': '统计',
    'report': '报告管理',
    'review': '我的审核',
    'multiPropertyReview': '审核',
    'organization': '机构管理',
    'dept': '部门管理',
    'business-sub-type': '业务子类型',
    'form-profile': '表单配置包',
    'cert-recognize-eval': '权属识别评测',
    'reportTemplate': '报告模板配置',
    'commonParams': '通用参数与子项',
    'district': '行政区划管理',
    'role': '角色管理',
    'permission': '权限管理',
    'menu': '菜单配置',
    'settings': '系统设置',
    'logs': '操作日志',
    'personal': '个人设置',
    'add': id ? '编辑' : '新增', // 如果有id参数，说明是编辑模式
    'netValueSolution': '净值方案',
    'planCustomParams': '自定义参数',
    'ledger': '台账管理',
    'inquiry': '询价台账',
    'list': '台账明细',
    'edit': id ? '编辑台账' : '新增台账', // 对于台账编辑页面，根据id判断
    'create': mode === 'edit' ? '编辑' : mode === 'view' ? '查看' : '新增',
    'view': '查看',
    'detail': '详情',
  };

  // 特殊路径处理：客户用户相关路径
  const getRouteName = (segment: string, context: string[]): string => {
    if (segment === 'statistics') {
      if (context.includes('task')) {
        return '任务统计';
      }
      if (context.includes('report')) {
        return '报告统计';
      }
    }
    // 如果是客户用户新增页面且有customerId参数，显示"用户详情"
    if (segment === 'user' && context.includes('customer') && customerId && context.includes('create')) {
      return '用户详情';
    }
    // 如果是客户用户相关路径，user应该显示为"客户管理"
    if (segment === 'user' && context.includes('customer')) {
      return '客户管理';
    }
    return routeNameMap[segment] || segment;
  };

  // 过滤掉数字ID和system父级路由
  const validSegments = segments.filter(segment => {
    if (/^\d+$/.test(segment)) return false;
    if (segment === 'system') return false;
    return true;
  });

  // 只取最后2级
  const displaySegments = validSegments.slice(-2);

  // 构建面包屑项
  let currentPath = '';
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    currentPath += `/${segment}`;

    if (displaySegments.includes(segment)) {
      let routeName = getRouteName(segment, segments);

      // 特殊处理：根据 from 参数调整面包屑名称
      // 1) 任务创建/编辑/查看页面的来源
      if (segment === 'task' && (segments.includes('create') || segments.includes('multiProperty')) && from) {
        if (from === 'myTask') {
          routeName = '我的任务';
        } else if (from === 'statistics') {
          routeName = '任务统计';
        } else if (from === 'taskSupplement') {
          routeName = '任务接单';
        } else if (from === 'reportReview') {
          routeName = '我的审核';
        } else if (from === 'reportStatistics') {
          routeName = '报告统计';
        }
      }
      // 2) 报告详情页（现为 task/detail）第一级展示：来自报告统计显示「报告管理」，来自我的审核显示「我的审核」
      if (segment === 'task' && segments.includes('detail') && from) {
        if (from === 'reportReview') {
          routeName = '我的审核';
        } else if (from === 'reportStatistics') {
          routeName = '报告管理';
        }
      }
      // 3) 多套审核/查看页，来源与单套详情一致
      if (segment === 'task' && segments.includes('multiPropertyReview') && from) {
        if (from === 'reportReview') {
          routeName = '我的审核';
        } else if (from === 'reportStatistics') {
          routeName = '报告管理';
        }
      }

      if (segment === 'list' && segments.includes('reportTemplate')) {
        routeName = '模板配置列表';
      }
      if (segment === 'create' || segment === 'add') {
        // 对于净值方案，如果有id参数，说明是编辑模式
        if (segment === 'add' && segments.includes('netValueSolution')) {
          routeName = id ? '编辑' : '新增';
        } else {
          routeName = mode === 'edit' ? '编辑' : mode === 'view' ? '查看' : '新增';
        }
      } else if (segment === 'multiProperty') {
        routeName = mode === 'view' ? '查看' : '编辑';
      } else if (segment === 'multiPropertyReview') {
        routeName = mode === 'view' ? '查看' : '审核';
      } else if (segment === 'edit') {
        // 对于台账编辑页面，根据id参数判断是新增还是编辑
        if (segments.includes('ledger')) {
          routeName = id ? '编辑台账' : '新增台账';
        } else if (segments.includes('taskSupplement')) {
          // 任务接单页：同一路径根据 mode 区分 编辑/查看
          routeName = mode === 'view' ? '查看' : '编辑';
        } else {
          routeName = '编辑';
        }
      } else if (segment === 'view') {
        routeName = '详情';
      } else if (segment === 'detail') {
        routeName = '详情';
      }

      if (routeName) {
        const isLast = segment === displaySegments[displaySegments.length - 1];

        // 对于编辑页面，点击应该跳转到对应的列表页面
        let targetPath = currentPath;
        if (segment === 'edit' && !isLast) {
          const parentPath = currentPath.replace('/edit', '');
          targetPath = parentPath;
        }

        // 特殊处理：客户用户新增页面的"用户详情"应该跳转回客户详情页面
        if (segment === 'user' && segments.includes('customer') && customerId && segments.includes('create') && !isLast) {
          targetPath = `/customer/view?id=${customerId}`;
        }

        // 特殊处理：如果是 task/create 或 task/multiProperty 页面，根据 from 参数设置跳转路径
        if (segment === 'task' && (segments.includes('create') || segments.includes('multiProperty')) && from && !isLast) {
          if (from === 'myTask') {
            targetPath = '/task/myTask';
          } else if (from === 'statistics') {
            targetPath = '/task/statistics';
          } else if (from === 'taskSupplement') {
            targetPath = '/task/taskSupplement';
          } else if (from === 'reportReview') {
            targetPath = '/task/review';
          } else if (from === 'reportStatistics') {
            targetPath = '/report/statistics';
          }
        }
        // 特殊处理：如果是 task/detail 页面（报告详情），根据 from 参数设置跳转路径
        if (segment === 'task' && segments.includes('detail') && from && !isLast) {
          if (from === 'reportReview') {
            targetPath = '/task/review';
          } else if (from === 'reportStatistics') {
            targetPath = '/report/statistics';
          }
        }
        if (segment === 'task' && segments.includes('multiPropertyReview') && from && !isLast) {
          if (from === 'reportReview') {
            targetPath = '/task/review';
          } else if (from === 'reportStatistics') {
            targetPath = '/report/statistics';
          }
        }

        breadcrumbItems.push({
          name: routeName,
          path: targetPath,
          clickable: !isLast,
        });
      }
    }
  }

  return breadcrumbItems;
};
